/**
 * Icon and font credits (R17). Most icons are CC BY 3.0 from game-icons.net,
 * which requires credit to the author and a link to the licence wherever the
 * icon is offered (docs/operations/game-icons-net-legal-review.md §2–3): the
 * picker's selected icon, the proof dialog, and the credits list. Each piece
 * renders nothing when the catalogue carries no credit, so the SPA is safe on
 * an engine that has not shipped the field yet.
 */

import type { Catalogue, IconCredit } from '../api/types.js';

const LICENSE_NAMES: Record<string, string> = {
    cc_by_3_0: 'CC BY 3.0',
    cc0: 'CC0 (public domain)',
};

function licenseName(credit: IconCredit): string {
    return LICENSE_NAMES[credit.licenseType] ?? credit.licenseType;
}

/**
 * Credit URLs are served exactly as stored, so only an http: or https: URL becomes
 * a link (defence in depth, Opus security review on .140); javascript:, data: and
 * anything else render as plain text. Not https-only: every author link on prod is
 * http:// (e.g. delapouite.com), and CC BY asks for the URI the licensor supplied.
 */
export function safeWebUrl(href: string | null | undefined): string | null {
    return typeof href === 'string' && /^https?:\/\//i.test(href) ? href : null;
}

function ExternalLink(props: { href: string | null; children: React.ReactNode }): React.JSX.Element {
    const href = safeWebUrl(props.href);
    if (href === null) {
        return <>{props.children}</>;
    }
    return (
        <a href={href} target="_blank" rel="noopener noreferrer">
            {props.children}
        </a>
    );
}

/** "Icon by Delapouite · CC BY 3.0", with the author and licence linked. */
export function IconCreditLine(props: { credit: IconCredit | null | undefined }): React.JSX.Element | null {
    const credit = props.credit;
    if (credit === null || credit === undefined) {
        return null;
    }
    return (
        <span className="credit-line">
            {credit.author !== null ? (
                <>Icon by <ExternalLink href={credit.authorUrl}>{credit.author}</ExternalLink> · </>
            ) : credit.attributionText !== null ? (
                <>{credit.attributionText} · </>
            ) : null}
            <ExternalLink href={credit.licenseUrl}>{licenseName(credit)}</ExternalLink>
        </span>
    );
}

/** Plain-text credit for a tooltip. */
export function iconCreditTitle(displayName: string, credit: IconCredit | null | undefined): string {
    if (credit === null || credit === undefined) {
        return displayName;
    }
    const by = credit.author !== null ? ` by ${credit.author}` : '';
    return `${displayName}${by} (${licenseName(credit)})`;
}

export function CreditsDialog(props: { catalogue: Catalogue; onClose: () => void }): React.JSX.Element {
    const icons = props.catalogue.icons.items.filter((i) => i.credit !== null && i.credit !== undefined);
    const fonts = props.catalogue.fonts.filter((f) => f.credit !== null && f.credit !== undefined);

    return (
        <div className="confirm-backdrop" role="dialog" aria-modal="true" aria-label="Credits">
            <div className="confirm proof">
                <h2>Credits</h2>
                <p className="confirm__lead">The icons and fonts in this designer are the work of these artists, used under their licences.</p>

                {icons.length === 0 && fonts.length === 0 && <p>No credits are available right now.</p>}

                {icons.length > 0 && (
                    <>
                        <h3 className="credits__heading">Icons</h3>
                        <ul className="credits__list">
                            {icons.map((icon) => (
                                <li key={icon.key}>
                                    <strong>{icon.displayName}</strong> — <IconCreditLine credit={icon.credit} />
                                </li>
                            ))}
                        </ul>
                    </>
                )}

                {fonts.length > 0 && (
                    <>
                        <h3 className="credits__heading">Fonts</h3>
                        <ul className="credits__list">
                            {fonts.map((font) => (
                                <li key={font.key}>
                                    <strong>{font.displayName}</strong> — {font.credit?.attributionText}
                                </li>
                            ))}
                        </ul>
                    </>
                )}

                <div className="confirm__actions">
                    <button type="button" className="confirm__back" onClick={props.onClose}>Close</button>
                </div>
            </div>
        </div>
    );
}
